import React, { Component } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import DashboardComponent from './dashboard.component';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import {
  getDashboardTasks,
  getExpenseFollowUp,
  getRecurringPayablesDashboard,
  getExternalComments,
  getExceptionComments,
  setDashboardFilters,
  openModal,
  closeModal,
  updateTask,
  getVoucherContext,
  getLetterTemplate,
  createManualTask,
  getRCFiles,
  getLetterTemplates,
  getTaskComments,
} from './dashboard.actions';
import {
  getActiveFiles,
  getCoveringFiles,
  getClosedFiles,
  getCurrentCoverage,
  assignCoverage,
  updateCoverage,
  cancelCoverage,
  getNotifications,
  dismissNotification,
  setCoverageModalOpen,
  setCoverageModalClosed,
} from 'modules/dashboard/myFiles/myFiles.actions';
import {
  GET_ACTIVE_FILES_FAILURE,
  GET_COVERING_FILES_FAILURE,
  GET_CLOSED_FILES_FAILURE,
  GET_CURRENT_COVERAGE_FAILURE,
  GET_NOTIFICATIONS_FAILURE,
} from 'modules/dashboard/myFiles/myFiles.types';
import { getAllEnumerations } from 'modules/enumerations/store/enumerationActions';
import { GET_ALL_ENUMERATIONS_SUCCESS } from 'modules/enumerations/store/enumeationTypes';
import { getActiveClients } from 'modules/clients/clients.actions';
import { GET_ACTIVE_CLIENTS_SUCCESS } from 'modules/clients/types';
import { postAuthorizationComment } from 'modules/authorizations/store/actions/authorizations.actions';
import { getVendors } from 'modules/authorizations/store/actions/dataManagement.actions';
import { GET_VENDORS_SUCCESS } from 'modules/authorizations/store/types/dataManagementTypes';
import { getInternalUsers } from 'modules/common/common.actions';
import { GET_INTERNAL_USERS_FAILURE } from 'modules/common/store/types';
import { TASK_MODAL_ACTION } from './components/modals/TaskCallModal.component';
import { TASK_STATUS } from 'modules/enumerations/enumerationIds';
import {
  GET_DASHBOARD_TASKS_FAILURE,
  GET_EXPENSE_FOLLOWUP_FAILURE,
  GET_RECURRING_PAYABLES_DASHBOARD_FAILURE,
  GET_EXTERNAL_COMMENTS_FAILURE,
  GET_EXCEPTION_COMMENTS_FAILURE,
  UPDATE_TASK_SUCCESS,
  CREATE_MANUAL_TASK_SUCCESS,
  GET_RC_FILES_SUCCESS,
  GET_LETTER_TEMPLATES_SUCCESS,
} from './types';

const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 25;

class DashboardContainer extends Component {
  static propTypes = {
    tasks: PropTypes.array,
    expenseFollowUp: PropTypes.array,
    recurringPayables: PropTypes.array,
    taskComments: PropTypes.object,
    isLoadingTasks: PropTypes.bool,
    isLoadingExpenses: PropTypes.bool,
    isLoadingRecurringPayables: PropTypes.bool,
    filters: PropTypes.object,
    activeModal: PropTypes.string,
    selectedTask: PropTypes.object,
    voucherContext: PropTypes.object,
    letterTemplate: PropTypes.object,
    userId: PropTypes.number,
    allEnums: PropTypes.object,
    internalUsers: PropTypes.array,
    setPageTitle: PropTypes.func.isRequired,
    showToast: PropTypes.func.isRequired,
    getDashboardTasks: PropTypes.func.isRequired,
    getExpenseFollowUp: PropTypes.func.isRequired,
    getRecurringPayablesDashboard: PropTypes.func.isRequired,
    setDashboardFilters: PropTypes.func.isRequired,
    setTaskFilters: PropTypes.func,
    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    updateTask: PropTypes.func.isRequired,

    getVoucherContext: PropTypes.func.isRequired,
    getLetterTemplate: PropTypes.func.isRequired,
    getActiveClients: PropTypes.func.isRequired,
    postAuthorizationComment: PropTypes.func.isRequired,
    getVendors: PropTypes.func.isRequired,
    createManualTask: PropTypes.func.isRequired,
    getRCFiles: PropTypes.func.isRequired,
    getLetterTemplates: PropTypes.func.isRequired,
    getTaskComments: PropTypes.func.isRequired,
    history: ReactRouterPropTypes.history.isRequired,
    unmappedClients: PropTypes.array,
    // Files data
    activeFiles: PropTypes.array,
    coveringFiles: PropTypes.array,
    closedFiles: PropTypes.array,
    closedFilesTotalCount: PropTypes.number,

    // Coverage data
    currentCoverage: PropTypes.array,
    notifications: PropTypes.array,

    // Loading states
    isLoadingActiveFiles: PropTypes.bool,
    isLoadingCoveringFiles: PropTypes.bool,
    isLoadingClosedFiles: PropTypes.bool,
    isLoadingCoverage: PropTypes.bool,
    isLoadingUsers: PropTypes.bool,
    isLoadingNotifications: PropTypes.bool,
    // Modal state
    isCoverageModalOpen: PropTypes.bool,
    isEditingCoverage: PropTypes.bool,
    existingCoverageData: PropTypes.object,
    error: PropTypes.string,

    assignCoverage: PropTypes.func.isRequired,
    updateCoverage: PropTypes.func.isRequired,
    cancelCoverage: PropTypes.func.isRequired,
    getNotifications: PropTypes.func.isRequired,
    setCoverageModalOpen: PropTypes.func.isRequired,
    setCoverageModalClosed: PropTypes.func.isRequired,
    dismissNotification: PropTypes.func.isRequired,
  };

  static defaultProps = {
    tasks: [],
    expenseFollowUp: [],
    recurringPayables: [],
    taskComments: {},
    isLoadingTasks: false,
    isLoadingExpenses: false,
    isLoadingRecurringPayables: false,
    filters: {},
    activeModal: null,
    selectedTask: null,
    voucherContext: null,
    letterTemplate: null,
    userId: null,
  };

  state = {
    isInitialLoad: true,
    clients: [],
    files: [],
    vendors: [],
    letterTemplates: [],
    internalUsers: [],
    selectedUserId: null, // Will be set to logged-in user on mount
  };

  componentDidMount() {
    this.props.setPageTitle('RC Dashboard');
    this.selectLoggedInUser();
  }

  componentDidUpdate(prevProps) {
    // The profile can arrive after mount; select the logged-in user once it does (as the EA dashboard does)
    if (!prevProps.userId && this.props.userId && !this.state.selectedUserId) {
      this.selectLoggedInUser();
    }
  }

  // Default the selected user to the logged-in user. The loaders read selectedUserId from state, so they run in the
  // setState callback; called straight after setState they saw null and nothing loaded until a user was picked.
  selectLoggedInUser = () => {
    const { userId } = this.props;
    if (!userId) return;

    this.setState({ selectedUserId: userId }, () => {
      this.loadFilterOptions();
      this.loadDashboardData();
    });
  };

  loadFilterOptions = async () => {
    const { selectedUserId } = this.state;

    if (!selectedUserId) return;

    // Load clients for filter dropdown
    const clientsResult = await this.props.getActiveClients();
    if (clientsResult.type === GET_ACTIVE_CLIENTS_SUCCESS) {
      this.setState({ clients: clientsResult.response || [] });
    }

    // Load vendors for task creation
    const vendorsResult = await this.props.getVendors();
    if (vendorsResult.type === GET_VENDORS_SUCCESS) {
      this.setState({ vendors: vendorsResult.response || [] });
    }

    // Load RC files (authorization summaries filtered by userId)
    const filesResult = await this.props.getRCFiles(selectedUserId);
    if (filesResult.type === GET_RC_FILES_SUCCESS) {
      this.setState({ files: filesResult.response || [] });
    }

    // Load letter templates
    const templatesResult = await this.props.getLetterTemplates();
    if (templatesResult.type === GET_LETTER_TEMPLATES_SUCCESS) {
      this.setState({ letterTemplates: templatesResult.response || [] });
    }

    // Load the enum data 
    const enumResult = await this.props.getAllEnumerations();
    if (enumResult.type !== GET_ALL_ENUMERATIONS_SUCCESS) {
      this.props.showToast('Failed to load enumerations', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }
  };

  loadDashboardData = async () => {
    const { selectedUserId } = this.state;

    if (!selectedUserId) {
      this.props.showToast('User ID not found. Please log in again.', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
      return;
    }

    // Load all internal users for the user selector
    const usersResult = await this.props.getInternalUsers();
    if (usersResult.type === GET_INTERNAL_USERS_FAILURE) {
      this.props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    }

    // Load tasks
    const tasksResult = await this.props.getDashboardTasks(selectedUserId);
    if (tasksResult.type === GET_DASHBOARD_TASKS_FAILURE) {
      this.props.showToast('Failed to load dashboard tasks', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Load expense follow-up
    const expensesResult = await this.props.getExpenseFollowUp(selectedUserId);
    if (expensesResult.type === GET_EXPENSE_FOLLOWUP_FAILURE) {
      this.props.showToast('Failed to load expense follow-up', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    const recurringPayablesResult = await this.props.getRecurringPayablesDashboard(selectedUserId);
    if (recurringPayablesResult.type === GET_RECURRING_PAYABLES_DASHBOARD_FAILURE) {
      this.props.showToast('Failed to load active recurring payables', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Load external comments
    const commentsResult = await this.props.getExternalComments(selectedUserId);
    if (commentsResult.type === GET_EXTERNAL_COMMENTS_FAILURE) {
      this.props.showToast('Failed to load external comments', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Load exception comments
    const exceptionsResult = await this.props.getExceptionComments(selectedUserId);
    if (exceptionsResult.type === GET_EXCEPTION_COMMENTS_FAILURE) {
      this.props.showToast('Failed to load exception comments', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Load active files
    const activeFiles = await this.props.getActiveFiles(selectedUserId);
    if (activeFiles.type === GET_ACTIVE_FILES_FAILURE) {
      this.props.showToast('Failed to load active files', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Load covering files
    const coveringFiles = await this.props.getCoveringFiles(selectedUserId);
    if (coveringFiles.type === GET_COVERING_FILES_FAILURE) {
      this.props.showToast('Failed to load covering files', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Load closed files
    const closedFiles = await this.props.getClosedFiles(selectedUserId, INITIAL_PAGE, INITIAL_ROWS_PER_PAGE);
    if (closedFiles.type === GET_CLOSED_FILES_FAILURE) {
      this.props.showToast('Failed to load closed files', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Load current coverage files
    const currentCoverage = await this.props.getCurrentCoverage(selectedUserId);
    if (currentCoverage.type === GET_CURRENT_COVERAGE_FAILURE) {
      this.props.showToast('Failed to load current coverage files', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Load current coverage files
    const notifications = await this.props.getNotifications(selectedUserId, 'CoverageAssigned');
    if (notifications.type === GET_NOTIFICATIONS_FAILURE) {
      this.props.showToast('Failed to load notifications', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    this.setState({ isInitialLoad: false });
  };

  handleTaskAction = async (task, actionType) => {
    // Handle action types from TaskRow component
    switch (actionType) {
      case 'call':
        // Load task comments, then open call modal
        await this.props.getTaskComments(task.taskId);
        this.props.openModal('call', task);
        break;

      case 'email':
        this.props.openModal('email', task);
        break;

      case 'letter':
        // Load template first, then open email modal
        await this.props.getLetterTemplate(task.taskId);
        this.props.openModal('email', task);
        break;

      case 'voucher':
        await this.handleVoucherAction(task);
        break;

      default:
        // Unknown action type - should not happen in normal operation
        this.props.showToast(`Unknown task action: ${actionType}`, {
          severity: TOAST_MESSAGE_SEVERITY_ERROR,
        });
    }
  };

  handleVoucherAction = async (task) => {
    // Navigate to vouchers page with taskId
    // The vouchers page will detect the taskId and auto-open the create modal with pre-filled data
    this.props.history.push(`/vouchers?taskId=${task.taskId}`);
  };

  handleUserChange = (newUserId) => {
    this.setState({ selectedUserId: newUserId }, () => {
      // Reload filter options (RC files are user-specific)
      this.loadFilterOptions();
      // Reload dashboard data with new user
      this.loadDashboardData();
    });
  };

  handleCallSubmit = async (taskAction, commentText, isClientViewable, taskStatusId) => {
    const { selectedTask } = this.props;
    let newValues = { ...selectedTask };

    if (!selectedTask) return;
    if (taskAction === TASK_MODAL_ACTION.ATTEMPT) {
      if (newValues.firstAttemptDate == null) { newValues.firstAttemptDate = new Date(); }
      if (taskStatusId) { newValues.taskStatusId = taskStatusId; }
    }
    else if (taskAction === TASK_MODAL_ACTION.COMPLETE) {
      if (newValues.completedDate == null) { newValues.completedDate = new Date(); }
      newValues.taskStatusId = TASK_STATUS.COMPLETED;
    }
    else {
      if (newValues.canceledDate == null) { newValues.canceledDate = new Date(); }
      newValues.taskStatusId = TASK_STATUS.CANCELED;
    }

    const result = await this.props.updateTask(newValues);

    if (result.type === UPDATE_TASK_SUCCESS) {
      // Create comment if user provided notes
      if (commentText && commentText.trim()) {
        const commentPayload = {
          authorizationId: selectedTask.fileId,
          taskId: selectedTask.taskId,
          serviceId: selectedTask.serviceId || null,
          regardingId: selectedTask.regardingId || null,
          subject: `${taskAction.subject}: ${selectedTask.title}`,
          comment: commentText,
          isTransfereeViewable: false,
          isClientViewable: !!isClientViewable,
        };

        const commentResult = await this.props.postAuthorizationComment(commentPayload, selectedTask.fileId);
        if (commentResult.error) {
          this.props.showToast('Task updated but failed to save comment', {
            severity: TOAST_MESSAGE_SEVERITY_ERROR,
          });
          this.props.closeModal();
          this.loadDashboardData();
          return;
        }
      }

      this.props.showToast(taskAction.successMessage, { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
      this.props.closeModal();
      this.loadDashboardData(); // Refresh
    } else {
      this.props.showToast('Failed to update task', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }
  };

  handleEmailSubmit = async (emailData) => {
    const { selectedTask } = this.props;
    let newValues = { ...selectedTask };

    if (!selectedTask) return;
    if (newValues.completedDate == null) { newValues.completedDate = new Date() }
    newValues.taskStatusId = '28125dac-5d57-4bb4-A92e-e411415fd8c3';

    // Step 1: Complete the task
    const result = await this.props.updateTask(selectedTask);

    if (result.type === UPDATE_TASK_SUCCESS) {
      // Step 2: Create comment with email details
      const emailBody = `
      To: ${emailData.toEmail}
      ${emailData.ccEmail ? `CC: ${emailData.ccEmail}\n` : ''}
      Subject: ${emailData.subject}

      ${emailData.bodyHtml}
      `.trim();

      const commentPayload = {
        authorizationId: selectedTask.fileId,
        taskId: selectedTask.taskId,
        serviceId: selectedTask.serviceId || null,
        regardingId: selectedTask.regardingId || null,
        subject: `Email Sent: ${emailData.subject}`,
        comment: emailBody,
        isTransfereeViewable: false,
        isClientViewable: false,
      };

      const commentResult = await this.props.postAuthorizationComment(commentPayload, selectedTask.fileId);
      if (commentResult.error) {
        this.props.showToast('Task completed but failed to save email record', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR,
        });
        this.props.closeModal();
        this.loadDashboardData();
        return;
      }

      // NOTE: Email sending is not yet implemented
      // This currently only logs the email content as a comment
      // TODO: Integrate with email sending service when available
      // Expected: POST to /api/emails/send with emailData payload

      this.props.showToast('Email logged and task completed (email sending pending implementation)', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS,
      });
      this.props.closeModal();
      this.loadDashboardData(); // Refresh
    } else {
      this.props.showToast('Failed to complete task', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }
  };

  handleCreateTaskModalOpen = () => {
    this.props.openModal('createTask', null);
  };

  handleCreateTask = async (formData) => {
    const { selectedUserId } = this.state;

    if (!selectedUserId) {
      this.props.showToast('User ID not found', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
      return;
    }

    // Build task payload
    const payload = {
      fileId: formData.fileId,
      segmentId: null, // Always null for manual tasks
      taskRuleId: null, // No rule since this is manually created
      taskTypeId: formData.taskTypeId,
      taskStatusId: '5640d88a-9546-45e2-b9f2-4aa81b178ee6', // Default to Active
      taskImportanceId: formData.importanceId,
      serviceId: formData.serviceId,
      regardingId: formData.regardingId,
      vendorId: formData.vendorId || null,
      letterTemplateId: formData.letterTemplateId || null,
      title: formData.taskText.substring(0, 100), // Truncate to 100 chars for title
      description: formData.taskText,
      dueDate: formData.dueDate, // Should be in ISO format (YYYY-MM-DD)
      firstAttemptDate: null,
      completedDate: null,
      canceledDate: null,
      triggerFieldName: '',
      triggerFieldValue: '',
      assignedToUserId: selectedUserId, // Assign to selected user
      isManuallyCreated: true,
      addedBy: selectedUserId,
      addedOn: new Date(),
      modifiedBy: selectedUserId,
      modifiedOn: new Date()
    };

    const result = await this.props.createManualTask(payload);

    if (result.type === CREATE_MANUAL_TASK_SUCCESS) {
      this.props.showToast('Task created successfully', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS,
      });
      this.props.closeModal();
      this.loadDashboardData(); // Refresh to show new task
    } else {
      this.props.showToast('Failed to create task', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }
  };

  render() {
    const {
      tasks,
      expenseFollowUp,
      recurringPayables,
      externalComments,
      exceptionComments,
      taskComments,
      filters,
      isLoadingTasks,
      isLoadingExpenses,
      isLoadingRecurringPayables,
      isLoadingExternalComments,
      isLoadingExceptionComments,
      activeModal,
      selectedTask,
      letterTemplate,
      setDashboardFilters,
      closeModal,
      allEnums,
      userId: loggedInUserId,
      internalUsers,
      unmappedClients
    } = this.props;

    const { isInitialLoad, files, vendors, letterTemplates, selectedUserId } = this.state;

    if (isInitialLoad && isLoadingTasks) {
      return <FullscreenSpinner />;
    }

    // Extract services and regardings from enumerations
    const allServices = allEnums?.authCommentService && allEnums.authCommentService.length > 0
      ? allEnums.authCommentService.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];
    const allRegardings = allEnums?.authCommentRegarding && allEnums.authCommentRegarding.length > 0
      ? allEnums.authCommentRegarding.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

    // The File filter and Create Task list the user's own files plus the files they are covering as the RC (once the
    // coverage has started, the same files My Tasks shows tasks for), labeled with whose file it is
    const coveredFiles = (this.props.coveringFiles || [])
      .filter(cf => cf.coveringRole === 'RC' && cf.hasStarted && !files.some(f => f.fileId === cf.fileId))
      .filter((cf, index, rcFiles) => rcFiles.findIndex(other => other.fileId === cf.fileId) === index)
      .map(cf => ({
        fileId: cf.fileId,
        fileNumber: cf.fileNumber,
        eeFullName: cf.eeFullName,
        coveringForName: `${cf.originalUserFirstName || ''} ${cf.originalUserLastName || ''}`.trim(),
      }));

    return (
      <DashboardComponent
        tasks={tasks}
        expenseFollowUp={expenseFollowUp}
        recurringPayables={recurringPayables}
        externalComments={externalComments}
        exceptionComments={exceptionComments}
        taskComments={taskComments}
        filters={filters}
        isLoading={isLoadingTasks}
        isLoadingExpenses={isLoadingExpenses}
        isLoadingRecurringPayables={isLoadingRecurringPayables}
        isLoadingExternalComments={isLoadingExternalComments}
        isLoadingExceptionComments={isLoadingExceptionComments}
        activeModal={activeModal}
        selectedTask={selectedTask}
        letterTemplate={letterTemplate}
        clients={unmappedClients || []}
        files={[...files, ...coveredFiles]}
        services={allServices}
        regardings={allRegardings}
        vendors={vendors}
        letterTemplates={letterTemplates}
        users={internalUsers || []}
        selectedUserId={selectedUserId}
        loggedInUserId={loggedInUserId}
        allEnums={allEnums}
        onFilterChange={setDashboardFilters}
        onTaskAction={this.handleTaskAction}
        onModalClose={closeModal}
        onCallSubmit={this.handleCallSubmit}
        onEmailSubmit={this.handleEmailSubmit}
        onCreateTask={this.handleCreateTask}
        onCreateTaskModalOpen={this.handleCreateTaskModalOpen}
        onUserChange={this.handleUserChange}
        activeFiles={this.props.activeFiles}
        coveringFiles={this.props.coveringFiles}
        closedFiles={this.props.closedFiles}
        currentCoverage={this.props.currentCoverage}
        notifications={this.props.notifications}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  tasks: state.dashboard.tasks,
  expenseFollowUp: state.dashboard.expenseFollowUp,
  recurringPayables: state.dashboard.recurringPayables,
  externalComments: state.dashboard.externalComments,
  exceptionComments: state.dashboard.exceptionComments,
  taskComments: state.dashboard.taskComments,
  isLoadingTasks: state.dashboard.isLoadingTasks,
  isLoadingExpenses: state.dashboard.isLoadingExpenses,
  isLoadingRecurringPayables: state.dashboard.isLoadingRecurringPayables,
  isLoadingExternalComments: state.dashboard.isLoadingExternalComments,
  isLoadingExceptionComments: state.dashboard.isLoadingExceptionComments,
  filters: state.dashboard.filters,
  activeModal: state.dashboard.activeModal,
  selectedTask: state.dashboard.selectedTask,
  voucherContext: state.dashboard.voucherContext,
  letterTemplate: state.dashboard.letterTemplate,
  userId: state.auth.profile?.internalUserId,
  allEnums: state.enumerations.allEnums,
  internalUsers: state.common.internalUsers,
  unmappedClients: state.clients.unmappedClients,
  // Files data
  activeFiles: state.myFiles.activeFiles,
  coveringFiles: state.myFiles.coveringFiles,
  closedFiles: state.myFiles.closedFiles,
  closedFilesTotalCount: state.myFiles.closedFilesTotalCount,

  // Coverage data
  currentCoverage: state.myFiles.currentCoverage,

  // Notifications
  notifications: state.myFiles.notifications,

  // Loading states
  isLoadingActiveFiles: state.myFiles.isLoadingActiveFiles,
  isLoadingCoveringFiles: state.myFiles.isLoadingCoveringFiles,
  isLoadingClosedFiles: state.myFiles.isLoadingClosedFiles,
  isLoadingCoverage: state.myFiles.isLoadingCoverage,
  isLoadingUsers: state.myFiles.isLoadingUsers,
  isLoadingNotifications: state.myFiles.isLoadingNotifications,

  // Modal state
  isCoverageModalOpen: state.myFiles.isCoverageModalOpen,
  isEditingCoverage: state.myFiles.isEditingCoverage,
  existingCoverageData: state.myFiles.existingCoverageData,
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    setPageTitle,
    showToast,
    getDashboardTasks,
    getExpenseFollowUp,
    getRecurringPayablesDashboard,
    getExternalComments,
    getExceptionComments,
    setDashboardFilters,
    openModal,
    closeModal,
    updateTask,
    getVoucherContext,
    getLetterTemplate,
    getActiveClients,
    postAuthorizationComment,
    getVendors,
    createManualTask,
    getRCFiles,
    getLetterTemplates,
    getTaskComments,
    getAllEnumerations,
    getInternalUsers,
    getActiveFiles,
    getCoveringFiles,
    getClosedFiles,
    getCurrentCoverage,
    getNotifications,
    assignCoverage,
    updateCoverage,
    cancelCoverage,
    setCoverageModalOpen,
    setCoverageModalClosed,
    dismissNotification,
  })
)(DashboardContainer);
